import { useState } from "react";

// Shared image placeholder primitives. Use these whenever an image may
// be missing or fail to load — products, orders, categories, etc. — so
// the UI shows a deterministic striped tile instead of the browser's
// broken-image affordance.

const PLACEHOLDER_TINTS = [
  "#1a1a1a",
  "#e8e4dc",
  "#c08a4a",
  "#7a8c9a",
  "#b8b0a4",
  "#3a4a5a",
  "#4a6a7a",
  "#6a7a4a",
  "#d6c7a8",
  "#bfae90",
  "#5a4a3a",
  "#9b8a78",
];

export function pickPlaceholderTint(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) | 0;
  }
  return PLACEHOLDER_TINTS[Math.abs(h) % PLACEHOLDER_TINTS.length];
}

type PlaceholderImageProps = {
  seed: string;
  label?: string;
  /** Defaults to filling the nearest positioned ancestor. */
  className?: string;
};

export function PlaceholderImage({
  seed,
  label,
  className = "absolute inset-0",
}: PlaceholderImageProps) {
  const tint = pickPlaceholderTint(seed);
  return (
    <div
      className={`${className} overflow-hidden`}
      style={{
        background: `linear-gradient(135deg, ${tint}22 0%, ${tint}55 100%)`,
      }}
      aria-hidden="true"
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `repeating-linear-gradient(45deg, ${tint}33 0 6px, transparent 6px 14px)`,
        }}
      />
      {label ? (
        <div
          className="absolute inset-0 flex items-center justify-center font-mono text-[9px] tracking-wide lowercase"
          style={{ color: `color-mix(in oklch, ${tint} 60%, #000)` }}
        >
          {label}
        </div>
      ) : null}
    </div>
  );
}

type ImageWithPlaceholderProps = {
  /** May be null/undefined when no image has been uploaded yet. */
  src: string | null | undefined;
  alt: string;
  seed: string;
  /** Shown on the placeholder when src is null (e.g. product slug). */
  label?: string;
  /**
   * Shown on the placeholder when src was provided but failed to load.
   * Use it to surface "Image not available" so the seller realises the
   * upload didn't render, instead of mistaking the stripes for an
   * intentional design choice.
   */
  failedLabel?: string;
  className?: string;
  onClick?: () => void;
};

/**
 * Renders an `<img>` and falls back to {@link PlaceholderImage} when the
 * src is empty OR the browser fails to load it (404, CORS, etc.). The
 * placeholder is only painted when no image is shown — when the image
 * loads successfully the caller's wrapper background fills any
 * transparent margins (relevant for `object-contain`). The caller is
 * responsible for the positioned ancestor — use {@link ImageThumb} when
 * you want a ready-made styled wrapper.
 */
export function ImageWithPlaceholder({
  src,
  alt,
  seed,
  label,
  failedLabel,
  className,
  onClick,
}: ImageWithPlaceholderProps) {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(src) && !failed;
  const placeholderLabel = failed && failedLabel ? failedLabel : label;
  return (
    <>
      {showImage ? (
        <img
          src={src ?? undefined}
          alt={alt}
          className={`${className ?? ""} relative`}
          onClick={onClick}
          onError={() => setFailed(true)}
        />
      ) : (
        <PlaceholderImage seed={seed} label={placeholderLabel} />
      )}
    </>
  );
}

type ImageThumbProps = {
  /** Image URL. May be null/undefined when no image is set. */
  src: string | null | undefined;
  /** Stable string for deterministic placeholder tint (id, slug, etc.). */
  seed: string;
  /** Alt text — defaults to empty so the thumb is decorative. */
  alt?: string;
  /** Tailwind size classes for the wrapper. Defaults to 26×26. */
  size?: string;
  /**
   * Tailwind background class for the wrapper. Defaults to the muted app
   * surface so order/dashboard chips read as inert. Pass `bg-white` (or
   * any arbitrary `bg-[...]`) for surfaces where the image should appear
   * to float on white, e.g. category tiles or product cards.
   */
  bg?: string;
  /** Optional label rendered on the placeholder when no image. */
  label?: string;
  /** Optional label when src was provided but failed to load. */
  failedLabel?: string;
  /** Extra wrapper classes (e.g. override border or rounded variant). */
  className?: string;
};

/**
 * Drop-in thumb with built-in fallback styling. Composes
 * {@link ImageWithPlaceholder} inside a bordered, rounded, muted-bg
 * wrapper so every list/card surface (order items, category tiles,
 * product chips…) renders a consistent striped tile when the upload
 * is missing or broken.
 */
export function ImageThumb({
  src,
  seed,
  alt = "",
  size = "h-[26px] w-[26px]",
  bg = "bg-[color:var(--background-muted)]",
  label,
  failedLabel,
  className,
}: ImageThumbProps) {
  return (
    <span
      aria-hidden={alt ? undefined : "true"}
      className={`relative block ${size} shrink-0 overflow-hidden rounded-md border border-[color:var(--border-light)] ${bg} ${className ?? ""}`}
    >
      <ImageWithPlaceholder
        src={src}
        alt={alt}
        seed={seed}
        label={label}
        failedLabel={failedLabel}
        className="h-full w-full object-contain"
      />
    </span>
  );
}
